import { useSeoMeta, useHead } from '@unhead/react'
import { useState } from 'react'
import { ExportButtons } from '@/components/ExportButtons'
import { ExportData } from '@/utils/exportUtils'
import { MaterialPricingPanel } from '@/components/MaterialPricingPanel'
import { useMaterialPricing } from '@/hooks/useMaterialPricing'

function ConcreteSlabCost() {
  useSeoMeta({
    title: 'Concrete Slab Cost Calculator | Foundation Cost Estimator',
    description: 'Calculate concrete slab foundation costs. Free cost calculator with material and labor estimates. Get accurate pricing for your slab project.',
    ogTitle: 'Concrete Slab Foundation Cost Calculator',
    ogDescription: 'Estimate concrete slab costs with materials and labor.',
    ogUrl: 'https://www.concrete-calculator.com/concrete-slab-cost/',
    ogType: 'website',
    twitterCard: 'summary_large_image'
  })

  useHead({
    link: [
      { rel: 'canonical', href: 'https://www.concrete-calculator.com/concrete-slab-cost/' }
    ]
  })

  const [length, setLength] = useState('')
  const [width, setWidth] = useState('')
  const [thickness, setThickness] = useState('')
  const [concretePrice, setConcretePrice] = useState('150') // $ per cubic yard
  const [laborPrice, setLaborPrice] = useState('6') // $ per sq ft
  const [showPricing, setShowPricing] = useState(false)
  const { getConcretePricePerYard, getLaborRate, getSitePrepRate, loading: pricingLoading } = useMaterialPricing('national', 'all')
  const [includePrep, setIncludePrep] = useState(true)
  const [results, setResults] = useState<{
    cubicYards: number
    squareFeet: number
    concreteCost: number
    laborCost: number
    prepCost: number
    totalCost: number
    costPerSqFt: number
  } | null>(null)

  const calculate = () => {
    const l = parseFloat(length)
    const w = parseFloat(width)
    const t = parseFloat(thickness) / 12 // Convert inches to feet
    const concretePerYard = parseFloat(concretePrice)
    const laborPerSqFt = parseFloat(laborPrice)

    if (isNaN(l) || isNaN(w) || isNaN(t) || l <= 0 || w <= 0 || t <= 0) {
      return
    }

    const squareFeet = l * w
    const cubicFeet = l * w * t
    const cubicYards = cubicFeet / 27

    // Cost calculations
    const concreteCost = cubicYards * concretePerYard
    const laborCost = squareFeet * laborPerSqFt
    
    // Site prep: $2-3 per sq ft (excavation, gravel base, vapor barrier)
    const prepCost = includePrep ? squareFeet * 2.5 : 0
    
    const totalCost = concreteCost + laborCost + prepCost
    const costPerSqFt = totalCost / squareFeet

    setResults({
      cubicYards: cubicYards,
      squareFeet: squareFeet,
      concreteCost: concreteCost,
      laborCost: laborCost,
      prepCost: prepCost,
      totalCost: totalCost,
      costPerSqFt: costPerSqFt
    })
  }

  const getExportData = (): ExportData => ({
    title: 'Concrete Slab Cost Calculator Results',
    inputs: [
      { label: 'Length', value: `${length} feet` },
      { label: 'Width', value: `${width} feet` },
      { label: 'Thickness', value: `${thickness} inches` },
      { label: 'Concrete Price', value: `$${concretePrice}/yd³` },
      { label: 'Labor Price', value: `$${laborPrice}/sq ft` },
      { label: 'Site Preparation', value: includePrep ? 'Included' : 'Not Included' }
    ],
    results: [
      { label: 'Project Area', value: `${results?.squareFeet.toFixed(0)} sq ft` },
      { label: 'Concrete Volume', value: `${results?.cubicYards.toFixed(2)} yd³` },
      { label: 'Concrete Material', value: `$${results?.concreteCost.toFixed(2)}` },
      { label: 'Labor Cost', value: `$${results?.laborCost.toFixed(2)}` },
      { label: 'Site Preparation', value: `$${results?.prepCost.toFixed(2)}` },
      { label: 'Total Project Cost', value: `$${results?.totalCost.toFixed(2)}` },
      { label: 'Cost per Square Foot', value: `$${results?.costPerSqFt.toFixed(2)}/sq ft` }
    ]
  })

  const reset = () => {
    setLength('')
    setWidth('')
    setThickness('')
    setConcretePrice('150')
    setLaborPrice('6')
    setIncludePrep(true)
    setResults(null)
  }

  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 style={{ fontSize: '2rem', color: '#4A4A4A', marginBottom: '1rem' }}>
          Concrete Slab Foundation Cost Calculator
        </h1>
        
        <nav aria-label="Calculator Tools" className="mb-8 flex items-center justify-between flex-wrap gap-3">
          <a href="/" className="text-[#FF8C00] hover:underline">← Back to Home</a>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setConcretePrice(getConcretePricePerYard().toFixed(2))
                setLaborPrice(getLaborRate().toFixed(2))
              }}
              disabled={pricingLoading}
              className="flex items-center gap-2 bg-[#FF8C00] text-white font-semibold py-2 px-4 rounded-md hover:bg-[#E67E00] transition-colors text-sm disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {pricingLoading ? 'Loading...' : 'Auto-Fill Live Prices'}
            </button>
            <button
              onClick={() => setShowPricing(!showPricing)}
              className="flex items-center gap-2 bg-[#4A4A4A] text-white font-semibold py-2 px-4 rounded-md hover:bg-[#3A3A3A] transition-colors text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {showPricing ? 'Hide' : 'View'} Price Guide
            </button>
          </div>
        </nav>

        {/* Material Pricing Panel */}
        {showPricing && (
          <div className="mb-8">
            <MaterialPricingPanel defaultCategory="all" />
          </div>
        )}

        {/* Calculator */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6 md:p-8 mb-12 shadow-sm">
          <h2 className="text-xl font-bold text-[#4A4A4A] mb-6">Estimate Slab Foundation Costs</h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#4A4A4A] mb-4">Slab Dimensions</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="length" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                  Length (feet)
                </label>
                <input
                  id="length"
                  type="number"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  placeholder="Enter length"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                  min="0"
                  step="0.1"
                />
              </div>

              <div>
                <label htmlFor="width" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                  Width (feet)
                </label>
                <input
                  id="width"
                  type="number"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  placeholder="Enter width"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                  min="0"
                  step="0.1"
                />
              </div>

              <div>
                <label htmlFor="thickness" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                  Thickness (inches)
                </label>
                <input
                  id="thickness"
                  type="number"
                  value={thickness}
                  onChange={(e) => setThickness(e.target.value)}
                  placeholder="e.g., 4"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                  min="0"
                  step="0.5"
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#4A4A4A] mb-4">Pricing (Local Rates)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="concretePrice" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                  Concrete Price ($ per cubic yard)
                </label>
                <input
                  id="concretePrice"
                  type="number"
                  value={concretePrice}
                  onChange={(e) => setConcretePrice(e.target.value)}
                  placeholder="150"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                  min="0"
                  step="5"
                />
              </div>

              <div>
                <label htmlFor="laborPrice" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                  Labor Price ($ per sq ft)
                </label>
                <input
                  id="laborPrice"
                  type="number"
                  value={laborPrice}
                  onChange={(e) => setLaborPrice(e.target.value)}
                  placeholder="6"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                  min="0"
                  step="0.5"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includePrep}
                  onChange={(e) => setIncludePrep(e.target.checked)}
                  className="w-5 h-5 text-[#FF8C00] border-gray-300 rounded focus:ring-[#FF8C00]"
                />
                <span className="text-sm font-semibold text-[#4A4A4A]">
                  Include site preparation costs ($2.50/sq ft for excavation, gravel, vapor barrier)
                </span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 mb-6">
            <button
              onClick={calculate}
              className="flex-1 bg-[#FF8C00] text-white font-bold py-3 px-6 rounded-md hover:bg-[#E67E00] transition-colors text-lg"
            >
              Calculate Cost
            </button>
            <button
              onClick={reset}
              className="flex-1 bg-gray-500 text-white font-bold py-3 px-6 rounded-md hover:bg-gray-600 transition-colors text-lg"
            >
              Reset
            </button>
          </div>

          {results && (
            <div className="bg-gray-50 border-2 border-[#FF8C00] rounded-lg p-6">
              <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
                <h3 className="text-xl font-bold text-[#4A4A4A]">Cost Breakdown</h3>
                <ExportButtons data={getExportData()} />
              </div>
              
              <div className="mb-4 p-4 bg-white rounded border-2 border-[#FF8C00]">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-[#4A4A4A]">Total Project Cost</span>
                  <span className="text-3xl font-bold text-[#FF8C00]">${results.totalCost.toFixed(2)}</span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  ${results.costPerSqFt.toFixed(2)} per square foot
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Project Area</p>
                  <p className="text-xl font-bold text-[#4A4A4A]">{results.squareFeet.toFixed(0)} sq ft</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Concrete Volume</p>
                  <p className="text-xl font-bold text-[#4A4A4A]">{results.cubicYards.toFixed(2)} yd³</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Concrete Material</p>
                  <p className="text-xl font-bold text-[#4A4A4A]">${results.concreteCost.toFixed(2)}</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Labor Cost</p>
                  <p className="text-xl font-bold text-[#4A4A4A]">${results.laborCost.toFixed(2)}</p>
                </div>
                {includePrep && (
                  <div className="bg-white p-4 rounded border border-gray-200 sm:col-span-2">
                    <p className="text-sm text-gray-600 mb-1">Site Preparation</p>
                    <p className="text-xl font-bold text-[#4A4A4A]">${results.prepCost.toFixed(2)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-6 text-sm text-gray-600">
            <p className="mb-2">
              <strong>How to use:</strong> Enter your slab dimensions and adjust local pricing. Default values reflect average U.S. costs. Concrete typically costs $125-175 per cubic yard, labor $5-8 per sq ft.
            </p>
            <p>
              <strong>Note:</strong> Actual costs vary by location, project complexity, and market conditions. Always get multiple quotes for accurate estimates.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <section aria-labelledby="faq-heading" className="mt-12">
          <h2 id="faq-heading" className="text-2xl font-bold text-[#4A4A4A] mb-4">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                How much does a 20x20 concrete slab cost?
              </h3>
              <p className="text-gray-700">
                A 20×20 foot (400 sq ft) concrete slab at 4 inches thick costs approximately $2,400-3,600 total. This includes materials ($600-900), labor ($2,000-2,400), and site prep ($600-1,000). Regional prices vary significantly.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                What affects concrete slab cost?
              </h3>
              <p className="text-gray-700">
                Key cost factors include: slab thickness (4" vs 6"), site accessibility, soil conditions requiring extra prep, reinforcement (rebar/wire mesh), finish type (broom vs. smooth), and local concrete delivery fees.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                Is it cheaper to pour concrete yourself?
              </h3>
              <p className="text-gray-700">
                DIY can save 40-60% on labor costs ($2,000-3,000 for a typical garage slab). However, it requires tools, equipment rental (mixer, forms), proper site prep knowledge, and physical labor. Small projects benefit most from DIY; large slabs often justify professional work.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default ConcreteSlabCost
